import { TESTDATA_MODAL } from '@enums/overlay-internal-events.enum';
import { TEST_DATA_SYMBOLS } from '@enums/test-data-symbols.enum';
import { TestDevelopmentContext } from '@modules/test-development/contexts/test-development-context';
import { VersionContext } from '@modules/test-development/contexts/version.context';
import {
  useAnimatedClass,
  useInternalEvents,
  useTranslation,
  getOrigin,
  NOOP,
  toastify,
} from '@testsigma/atoms';
import { TestCase } from '@modules/test-development/models/test-case.model';
import { ParameterValueSelector } from '@testsigma/organism';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  TestDataDrawerHelpInfoProps,
  TestDataDrawerLayout,
} from './test-data-drawer-layout';
import { getBaseUrl } from '@api/instance';
import { useTestDataFindAll } from '@shared/services/test-data.service';

export const ParameterDrawer = ({
  tdpIds,
  testCaseId,
  profileList,
  context,
  isInsideRestApi = false,
  refetchTestCase,
}) => {
  const { t } = useTranslation();
  const testDevelopmentContext = useContext(TestDevelopmentContext);
  const versionContext = useContext(VersionContext);
  const version = testDevelopmentContext?.version || versionContext?.version;
  const publishInternalEvents = useInternalEvents([]);
  const [selectedDataProfileId, updateSelectedDataProfileId] = useState(null);
  const [tdpIdList, setTdpIdList] = useState(tdpIds);

  const { data: dataProfile, isFetching } = useTestDataFindAll({
    payload: {
      query: `id@${tdpIdList}`,
      size: (tdpIdList || '').split('#').length,
    },
    enabled: !!tdpIdList,
    callback: NOOP,
    applyCache: true,
  });

  const tdpProfileList = useMemo(() => {
    // Sorting the list based on the step number
    const filteredItem = profileList?.filter((p) => !p.stepNumber) || [];
    const filterData = [
      ...(profileList?.filter((p) => p.stepNumber)?.reverse() || []),
      ...filteredItem,
    ];
    return filterData?.length ? filterData : dataProfile?.content;
  }, [profileList, dataProfile]);

  const onClose = useCallback(
    () =>
      publishInternalEvents(
        TESTDATA_MODAL.CLOSE_PARAMETER,
        isInsideRestApi ? { isInsideRestApi: true } : null,
      ),
    [publishInternalEvents],
  );

  const onDataClick = useCallback(
    (item, tdp) => {
      publishInternalEvents(TESTDATA_MODAL.SELECTED_PARAMETER, {
        item: item,
        tdp: tdp,
        context,
      });
    },
    [publishInternalEvents],
  );

  useEffect(() => {
    updateSelectedDataProfileId(
      (
        dataProfile?.content?.find(
          (item) => item.id == tdpProfileList?.[0]?.id,
        ) || dataProfile?.content?.[0]
      )?.id,
    );
  }, [dataProfile, tdpProfileList]);

  const reFetchTestCase = () => {
    refetchTestCase()?.then((res: TestCase) => {
      setTimeout(
        () => {
          if (res?.testCaseDataDrivenCondition?.testDataProfileId) {
            setTdpIdList(
              res?.testCaseDataDrivenCondition?.testDataProfileId?.toString(),
            );
            updateSelectedDataProfileId(
              res?.testCaseDataDrivenCondition?.testDataProfileId,
            );
          } else {
            toastify(t('modal.help.parameter.not_found'), { type: 'warning' });
          }
        },
        200,
        res,
      );
    });
  };

  const openEditTestCase = useCallback(async () => {
    const isMobileRecorder =
      window.location.pathname.includes('record/steps') ||
      window.location.pathname.includes('record/stepgroup:true/steps') ||
      window.location.pathname.includes('/ui/recording') ||
      window.location.pathname.includes('/td/debugrun/');

    if (isMobileRecorder) {
      window.open(
        `${getOrigin()}/ui/td/${
          version.id
        }/cases/filters/${testCaseId}?config=true`,
        '_blank',
      );
    } else {
      onClose();
      publishInternalEvents('associate-tdp', null, true);
      publishInternalEvents('open-test-case-settings', null);
    }
  }, [publishInternalEvents, testCaseId, version?.id]);

  const openTestDataProfile = useCallback(async () => {
    const baseURL = await getBaseUrl();
    window.open(`${baseURL}/ui/td/${version.id}/data/new`, '_blank');
  }, []);

  const helpInfoProps: TestDataDrawerHelpInfoProps = {
    description: t('modal.help.parameter'),
    url: 'https://testsigma.com/docs/test-data/create-data-profiles/',
  };

  const animationClass = useAnimatedClass('reval-animation-opacity');

  return (
    <TestDataDrawerLayout
      title={t('modal.title.parameter')}
      symbol={TEST_DATA_SYMBOLS.PARAMETER}
      onClose={onClose}
      helpInfo={helpInfoProps}
    >
      <div className={classNames('px-5 py-6 h-full', animationClass)}>
        <ParameterValueSelector
          selectedDataProfileId={selectedDataProfileId}
          onchangeProfile={(item) => updateSelectedDataProfileId(item?.id)}
          dataProfiles={dataProfile?.content || []}
          onClick={onDataClick}
          isFetching={isFetching}
          openEditTestCase={openEditTestCase}
          openTestDataProfile={openTestDataProfile}
          profileList={tdpProfileList || []}
          fetchDataProfile={reFetchTestCase}
        />
      </div>
    </TestDataDrawerLayout>
  );
};
